import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { SignalChip } from '@/components/shared/SignalChip';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO } from '@/components/shared/SEO';
import { Check, ArrowRight, Sparkles, ChevronDown } from 'lucide-react';
import { submitWaitlistForm } from '@/lib/api';
import { PromoSpotsDisplay } from '@/components/PromoSpotsDisplay';

const waitlistData = {
  intelligence: {
    badge: 'Shopify App + Web Dashboard',
    title: 'See which competitor ads actually work — before you waste budget testing',
    subtitle: 'Track competitor ads and products daily. Get the Pro plan at Basic pricing + be first to get AI feedback on YOUR campaigns.',
    trustLine: 'Built exclusively for Shopify brands',
    problemHeading: 'Stop scaling blindly while competitors pull ahead',
    problems: [
      'Testing ad creatives blindly while competitors already know what works',
      'Missing price changes and new products until customers tell you',
      'Manually checking competitor sites and ad libraries every day'
    ],
    features: [
      {
        title: 'Competitor Store Monitoring',
        description: 'Track any Shopify store\'s moves in real-time'
      },
      {
        title: 'Ad Intelligence',
        description: 'Meta and Google ads with winning creative detection'
      },
      {
        title: 'Product Launch Detection',
        description: 'Know what they\'re launching before customers do'
      },
      {
        title: 'Price Change Tracking',
        description: 'Daily monitoring of competitor price changes'
      }
    ],
    howItWorks: [
      'Install RADAR and set your niche',
      'We auto-discover and monitor competitors daily',
      'See winning ads after 7-14 days of tracking'
    ],
    outcomes: [
      'Test only proven creative angles',
      'Know what products competitors are pushing',
      'See price changes as they happen',
      'Make data-driven decisions, not guesses'
    ],
    perks: [
      '🏆 Pro plan features at Basic pricing ($49 vs $99)',
      '🎆 First access to TikTok ads tracking when ready',
      '🎯 Connect YOUR Meta & Google ad accounts for AI feedback',
      '📊 Get performance insights on YOUR actual campaigns',
      '🚀 Early access to influencer monitoring platform',
      '💰 Priority access to affiliate tracking platform'
    ],
    useCases: [
      'Track competitor products & ads',
      'Find winning products',
      'Find winning ads for my store',
      'Research the market',
      'Find market opportunities'
    ]
  },
  research: {
    badge: 'Shopify App + Web Dashboard',
    title: 'Find winning ads and products that actually convert',
    subtitle: 'See which competitor ads work PLUS get AI feedback on YOUR ads. Lock in Pro features at Basic pricing and first access to ad account integration.',
    trustLine: 'Built exclusively for Shopify brands',
    problemHeading: 'Stop guessing what works while competitors scale winners',
    problems: [
      'Wasting budget on creative angles that don\'t convert',
      'Missing competitor price moves and product launches',
      'Testing the same failed concepts your competitors already tried'
    ],
    features: [
      {
        title: 'Winning Creative Discovery',
        description: 'Find ads that convert before they saturate'
      },
      {
        title: 'Product Opportunity Spotting',
        description: 'Discover products trending upward'
      },
      {
        title: 'Daily Sync',
        description: 'Automatic daily updates of ads and products'
      },
      {
        title: 'Creative Trend Analysis',
        description: 'Know what formats are winning now'
      }
    ],
    howItWorks: [
      'Install RADAR and set your niche',
      'We auto-discover and monitor competitors daily',
      'See winning ads after 7-14 days of tracking'
    ],
    outcomes: [
      'Test creative angles that actually work',
      'Track competitor product moves daily',
      'Know which ads survive multi-variant tests',
      'Stop guessing, start knowing'
    ],
    perks: [
      '🏆 Pro plan features at Basic pricing ($49 vs $99)',
      '🎆 First access to TikTok ads tracking when ready',
      '🎯 Connect YOUR Meta & Google ad accounts for AI feedback',
      '📊 Get performance insights on YOUR actual campaigns',
      '🚀 Early access to influencer monitoring platform',
      '💰 Priority access to affiliate tracking platform'
    ],
    useCases: [
      'Track competitor products & ads',
      'Find winning products',
      'Find winning ads for my store',
      'Research the market',
      'Find market opportunities'
    ]
  },
  // Legacy redirects - keeping for backward compatibility
  google: {
    badge: 'Market Intelligence',
    title: 'Track competitors\' products, ads, pricing and launches — built for Shopify brands',
    subtitle: 'See what your competitors launch before your customers do. Monitor price changes, stock levels, and marketing campaigns in one dashboard.',
    perks: [
      '50% off all plans for first 3 months',
      '1:1 strategy consultation with our team',
      'Priority access to new features'
    ],
    features: [
      'Competitor product tracking',
      'Real-time price monitoring',
      'Launch alerts & notifications'
    ],
    useCases: [
      'Track competitor products & ads',
      'Find winning products',
      'Find winning ads for my store',
      'Research the market',
      'Find market opportunities'
    ]
  },
  reddit: {
    badge: 'Market Intelligence',
    title: 'Track competitors\' products, ads, pricing and launches — built for Shopify brands',
    subtitle: 'See what your competitors launch before your customers do. Monitor price changes, stock levels, and marketing campaigns in one dashboard.',
    perks: [
      '50% off all plans for first 3 months',
      '1:1 strategy consultation with our team',
      'Priority access to new features'
    ],
    features: [
      'Competitor product tracking',
      'Real-time price monitoring',
      'Launch alerts & notifications'
    ],
    useCases: [
      'Track competitor products & ads',
      'Find winning products',
      'Find winning ads for my store',
      'Research the market',
      'Find market opportunities'
    ]
  },
  x: {
    badge: 'Market Intelligence',
    title: 'Track competitors\' products, ads, pricing and launches — built for Shopify brands',
    subtitle: 'See what your competitors launch before your customers do. Monitor price changes, stock levels, and marketing campaigns in one dashboard.',
    perks: [
      '50% off all plans for first 3 months',
      '1:1 strategy consultation with our team',
      'Priority access to new features'
    ],
    features: [
      'Competitor product tracking',
      'Real-time price monitoring',
      'Launch alerts & notifications'
    ],
    useCases: [
      'Track competitor products & ads',
      'Find winning products',
      'Find winning ads for my store',
      'Research the market',
      'Find market opportunities'
    ]
  },
};

export default function WaitlistNew({ platform: platformProp }) {
  const params = useParams();
  const platform = params.platform || platformProp || 'intelligence';
  const data = waitlistData[platform] || waitlistData.intelligence;
  const [email, setEmail] = useState('');
  const [selectedUseCase, setSelectedUseCase] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  const selectUseCase = (useCase) => {
    setSelectedUseCase(useCase);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setError('');

    try {
      await submitWaitlistForm({
        email: email.trim(),
        variant: platform,
        selectedUseCase: selectedUseCase || null
      });

      // Claim a spot
      try {
        await fetch('http://localhost:3000/api/promo-spots', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.trim(),
            claimed: true
          })
        });
      } catch (error) {
        console.log('Could not update spots count');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Waitlist submission failed:', error);
      setError(error.response?.data?.error || 'Failed to join waitlist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={
          platform === 'intelligence'
            ? 'Shopify Competitor Intelligence App | RADAR Web Dashboard Early Access'
            : platform === 'research'
            ? 'Shopify Product Research App | Find Winning Products & Ads'
            : `Join Early Access — ${data.badge}`
        }
        description={data.subtitle}
        canonical={`/waitlist/${platform}`}
        keywords={
          platform === 'intelligence'
            ? 'shopify competitor tracking, shopify market intelligence, competitor monitoring app'
            : platform === 'research'
            ? 'shopify product research, winning products, ad creatives, product discovery'
            : undefined
        }
        noindex
      />

      {/* Hero Section */}
      <Section hero className="min-h-screen flex items-center">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left Side - Hero Content */}
            <div className="order-2 lg:order-1">
              <AnimatedReveal>
                <SignalChip variant="lime" className="mb-6">
                  <Sparkles className="h-3 w-3" /> {data.badge}
                </SignalChip>

                <h1 className="text-4xl lg:text-5xl font-display font-bold tracking-tight text-foreground mb-6">
                  {data.title}
                </h1>
                <p className="text-lg text-muted-foreground mb-8">{data.subtitle}</p>

                {/* Trust Line */}
                <div className="flex items-center gap-2 mb-8">
                  <div className="h-2 w-2 rounded-full bg-lime-500"></div>
                  <p className="text-sm font-medium text-lime-500">{data.trustLine}</p>
                </div>
              </AnimatedReveal>
            </div>

            {/* Right Side - Form */}
            <div className="order-1 lg:order-2">
              <AnimatedReveal>
                {!submitted ? (
                  <GlassPanel className="p-8 relative overflow-hidden">
                    {/* Value prop banner */}
                    <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-r from-lime-500/20 to-lime-500/10 border-b border-lime-500/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-mono uppercase text-lime-400 mb-1">Waitlist Exclusive</p>
                          <p className="text-lg font-bold text-foreground">$99 Pro Plan</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground line-through">Regular $99/mo</p>
                          <p className="text-2xl font-bold text-lime-400">$49/mo</p>
                        </div>
                      </div>
                      <p className="text-xs text-lime-400/90 mt-2">Save $50/month forever — Unlimited competitors + AI feedback</p>
                    </div>
                    <div className="mt-20">
                      <h2 className="text-2xl font-display font-bold text-foreground mb-3">Lock in Pro pricing forever</h2>
                      <div className="mb-6">
                        <PromoSpotsDisplay variant="form" />
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="waitlist-form">
                      <div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4 text-base text-foreground placeholder-muted-foreground outline-none focus:border-lime-500/30"
                          data-testid="waitlist-email-input"
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting || !email.trim()}
                        className="w-full py-4 rounded-xl bg-[#CCFF6C] text-black text-base font-semibold hover:bg-[#D9FF9A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="waitlist-submit-button"
                      >
                        {isSubmitting ? 'Joining...' : 'Get Pro Plan at $49/mo'}
                      </button>

                      {error && (
                        <p className="text-red-400 text-sm">{error}</p>
                      )}
                    </form>

                    {/* Use Case Selection */}
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <p className="text-sm font-medium text-foreground mb-4">What will you use RADAR for?</p>
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className="w-full text-left p-4 rounded-lg border border-white/10 bg-white/[0.03] text-sm transition-all duration-200 hover:border-lime-500/30 hover:bg-white/[0.05] flex items-center justify-between"
                        >
                          <span className={selectedUseCase ? 'text-foreground' : 'text-foreground/50'}>
                            {selectedUseCase || 'Select your primary use case...'}
                          </span>
                          <ChevronDown className={`h-4 w-4 text-foreground/50 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-background/95 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden z-[9999]">
                            {data.useCases.map((useCase, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => selectUseCase(useCase)}
                                className="w-full text-left p-4 text-sm text-foreground/80 hover:bg-lime-500/10 hover:text-foreground transition-colors border-b border-white/5 last:border-b-0"
                              >
                                {useCase}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </GlassPanel>
                ) : (
                  <GlassPanel className="p-8 text-center">
                    <div className="flex items-center justify-center mb-6">
                      <div className="h-16 w-16 rounded-full bg-lime-500/10 border border-lime-500/20 flex items-center justify-center">
                        <Check className="h-8 w-8 text-lime-500" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-display font-bold text-foreground mb-3">You're on the list! 🎉</h3>
                    <p className="text-base text-muted-foreground mb-6">
                      We'll notify you at <span className="text-foreground font-medium">{email}</span> when RADAR launches.
                    </p>
                    {selectedUseCase && (
                      <div className="mb-6 pt-4 border-t border-white/10">
                        <p className="text-sm text-muted-foreground mb-3">Your primary use case:</p>
                        <div className="flex justify-center">
                          <span className="px-4 py-2 bg-lime-500/10 border border-lime-500/20 rounded-md text-sm text-lime-400">
                            {selectedUseCase}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-lime-500">Your early access is confirmed:</strong> Web dashboard + Shopify app + exclusive perks
                      </p>
                    </div>
                  </GlassPanel>
                )}
              </AnimatedReveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* Problem Section */}
      <Section className="py-24">
        <Container>
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedReveal>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-8">
                {data.problemHeading}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {data.problems.map((problem, i) => (
                  <GlassPanel key={i} className="p-6">
                    <p className="text-muted-foreground text-center">{problem}</p>
                  </GlassPanel>
                ))}
              </div>
            </AnimatedReveal>
          </div>
        </Container>
      </Section>

      {/* Features Section */}
      <Section className="py-24">
        <Container>
          <div className="max-w-6xl mx-auto">
            <AnimatedReveal>
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4">
                  Complete competitor visibility in one dashboard
                </h2>
                <p className="text-lg text-muted-foreground">
                  Everything you need to stay ahead of your competition
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {data.features.map((feature, i) => (
                  <GlassPanel key={i} className="p-6 text-center">
                    <h3 className="text-lg font-semibold text-foreground mb-3">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </GlassPanel>
                ))}
              </div>
            </AnimatedReveal>
          </div>
        </Container>
      </Section>

      {/* How It Works Section */}
      <Section className="py-24">
        <Container>
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedReveal>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-16">
                How RADAR works
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {data.howItWorks.map((step, i) => (
                  <div key={i} className="relative">
                    <div className="flex items-center justify-center mb-6">
                      <div className="h-12 w-12 rounded-full bg-lime-500/10 border border-lime-500/20 flex items-center justify-center">
                        <span className="text-lime-500 font-bold text-lg">{i + 1}</span>
                      </div>
                    </div>
                    <p className="text-muted-foreground">{step}</p>
                    {i < data.howItWorks.length - 1 && (
                      <ArrowRight className="h-6 w-6 text-muted-foreground/30 absolute top-6 -right-4 hidden md:block" />
                    )}
                  </div>
                ))}
              </div>
            </AnimatedReveal>
          </div>
        </Container>
      </Section>

      {/* Outcomes Section */}
      <Section className="py-24">
        <Container>
          <div className="max-w-4xl mx-auto">
            <AnimatedReveal>
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4">
                  Make decisions with complete market visibility
                </h2>
                <p className="text-lg text-muted-foreground">
                  Stop guessing. Start knowing what works.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {data.outcomes.map((outcome, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <Check className="h-6 w-6 text-lime-500 flex-shrink-0 mt-1" />
                    <p className="text-lg text-foreground">{outcome}</p>
                  </div>
                ))}
              </div>
            </AnimatedReveal>
          </div>
        </Container>
      </Section>

      {/* Launch Perks Section */}
      <Section className="py-24">
        <Container>
          <div className="max-w-4xl mx-auto">
            <AnimatedReveal>
              <GlassPanel className="p-8">
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Sparkles className="h-5 w-5 text-lime-500" />
                    <p className="text-sm font-mono uppercase tracking-wider text-lime-500">Launch perks worth $500+</p>
                  </div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-2">
                    Early access benefits
                  </h2>
                  <p className="text-muted-foreground">
                    Available only to waitlist members
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {data.perks.map((perk, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-lime-500 flex-shrink-0 mt-1" />
                      <span className="text-foreground">{perk}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-white/10 text-center">
                  <p className="text-sm text-muted-foreground">
                    Launch pricing ends after first 100 customers
                  </p>
                </div>
              </GlassPanel>
            </AnimatedReveal>
          </div>
        </Container>
      </Section>

      {/* Final CTA Section */}
      <Section className="py-24">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedReveal>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-6">
                Start seeing what your competitors are doing before everyone else
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Join 500+ Shopify brands getting early access to RADAR
              </p>
              {!submitted && (
                <GlassPanel className="p-8 max-w-md mx-auto">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4 text-base text-foreground placeholder-muted-foreground outline-none focus:border-lime-500/30"
                      required
                      disabled={isSubmitting}
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting || !email.trim()}
                      className="w-full py-4 rounded-xl bg-[#CCFF6C] text-black text-base font-semibold hover:bg-[#D9FF9A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Joining...' : 'Reserve Your Spot'}
                    </button>
                  </form>
                </GlassPanel>
              )}
            </AnimatedReveal>
          </div>
        </Container>
      </Section>
    </>
  );
}
